/* eslint-disable no-nested-ternary */
/* eslint-disable no-shadow */
/* eslint-disable indent */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { useForm } from 'react-hook-form';
import { Tag } from 'primereact/tag';

import { Can } from 'components/Can';
import ErrorDialog from 'components/ErrorDialog';
import Members from 'features/Council/components/Detail/Members';
import {
  checkIsAllowedCRUDFromContract,
  getRoles,
  formatFileName,
  getAcadamicLevelShortHand,
  formatDatetime,
  is1593,
  roundNumber,
  autoDownloadFile,
} from 'utils/func';

import {
  downloadFile,
  getAllCouncilTypes,
  getAcceptanceCouncilOfProjectProposal,
  downloadCouncilMemberExcelFile,
} from 'apis/councils.api';

import { getAll as getAllOrganizations } from 'apis/organizations.api';
import { getAllVersions, getReviseLogs, updateExplanationRequest } from 'apis/projectProposals.api';
import { getGeneralInfoForCouncilById } from 'apis/projects.api';

import { COUNCIL_TYPE_CODE, PROJECT_ROLES, TIMEZONE_VN, TOAST } from 'constant';
import SendMailRemindAllMembersDialog from 'features/Council/components/SendRemindMembersDialog';
import Loading from 'components/Loading';

import { toast } from 'layout';

import { TextareaInput } from 'components/FormControl';
import RadioInput from 'components/FormControl/RadioInput';
import RichTextViewer from 'components/RichTextViewer';

export default function AcceptanceCouncil() {
  // #region Data
  const { t } = useTranslation();
  const roles = getRoles();

  const { projectId, summaryReportId } = useParams();
  const navigate = useNavigate();

  const { data: councilTypesData } = useQuery(['councilTypes'], getAllCouncilTypes, {
    staleTime: 5000 * 60,
  });
  // eslint-disable-next-line no-unused-vars
  const councilType = useMemo(() => {
    if (councilTypesData?.data?.length > 0) {
      return councilTypesData?.data?.find((item) => item?.code === 'acceptanceCouncil') ?? null;
    }
    return null;
  }, [councilTypesData?.data, t]);

  // General info of project  (section A)
  const { data: generalInfoProjectData } = useQuery(
    ['GeneralInfoProject', projectId],
    () => getGeneralInfoForCouncilById(projectId),
    {
      staleTime: 1000 * 60 * 5,
    }
  );
  const generalInfoProject = useMemo(() => generalInfoProjectData?.data, [generalInfoProjectData]);

  const projectProposalId = useMemo(
    () => generalInfoProject?.projectProposal?.reference ?? null,
    [generalInfoProject?.projectProposal?.reference]
  );
  const owner = useMemo(
    () =>
      generalInfoProject?.projectProposal?.projectProposalContacts?.find(
        (item) => item?.projectRole?.code === PROJECT_ROLES.OWNER
      ) ?? {},
    [generalInfoProject]
  );
  // end General info of project proposal (section A)

  // Council
  const {
    data: councilData,
    isLoading,
    refetch: refetchCouncil,
  } = useQuery(
    ['acceptanceCouncil', projectProposalId],
    () => getAcceptanceCouncilOfProjectProposal({ projectProposalId }),
    {
      enabled: !!projectProposalId,
    }
  );

  const council = useMemo(() => councilData?.data || null, [councilData]);
  // end Review

  // Organizations (to get organization name for every council member)
  const { data: rawOrganizations } = useQuery(['Organizations'], getAllOrganizations, {
    staleTime: 1000 * 60 * 5,
  });
  const organizations = useMemo(
    () =>
      rawOrganizations?.data.map((e) => ({
        ...e,
        name: t('lang') === 'vi' ? e?.name : e?.englishName,
      })) ?? [],
    [rawOrganizations, t]
  );
  // end Organizations

  const {
    handleSubmit: handleSubmitFeedback,
    control: feedbackControl,
    reset: resetFeedback,
    formState: {
      errors: feedbackErrors,
      dirtyFields: feedbackDirtyFields,
      isDirty: isFeedbackDirty,
      isValid: isFeedbackValid,
    },
  } = useForm({ mode: 'onChange' });

  const {
    data: explanationRequestsData,
    isLoading: isExplanationRequestsLoading,
    // isError: isExplanationRequestsError,
    // error: explanationRequestsError,
    refetch: refetchExplanationRequests,
  } = useQuery(
    [
      'projectProposalReviseLogs',
      projectProposalId,
      council?.councilType?.code,
      'ALL_REQUEST_EXPLANATION_ACCEPTANCE_REPORT',
    ],
    () => {
      let type = null;
      if (council?.councilType?.code === COUNCIL_TYPE_CODE.SPECIALIZATION_COUNCIL) {
        type = 'REQUEST_EXPLANATION_SPECIALIZATION';
      }
      if (council?.councilType?.code === COUNCIL_TYPE_CODE.FINANCE_COUNCIL) {
        type = 'REQUEST_EXPLANATION_FINANCE';
      }
      if (council?.councilType?.code === COUNCIL_TYPE_CODE.MIDTERM_REPORT_COUNCIL) {
        type = 'REQUEST_EXPLANATION_MIDTERM_REPORT';
      }
      if (council?.councilType?.code === COUNCIL_TYPE_CODE.ACCEPTANCE_COUNCIL) {
        type = 'REQUEST_EXPLANATION_ACCEPTANCE_REPORT';
      }
      return getReviseLogs({
        projectProposalId,
        types: [type],
      });
    },
    { enabled: !!projectProposalId && !!council?.councilType?.code }
  );

  const explanationRequest = useMemo(() => {
    if (explanationRequestsData?.data?.length > 0) {
      return explanationRequestsData?.data[0];
    }
    return null;
  }, [explanationRequestsData]);

  const [isVisibleErrorDialog, setIsVisibleDialogError] = useState(false);
  const {
    mutate: mutateDownloadFile,
    error: errorResDownloadFile,
    isError: isErrorResDownloadFile,
  } = useMutation(downloadFile);
  const [isVisibleConfirmSendRemind, setIsVisibleConfirmSendRemind] = useState(false);

  const {
    mutate: mutateUpdateExplanationRequest,
    isLoading: isUpdateExplanationRequestLoading,
    // isError: isUpdateExplanationRequestError,
    // error: updateExplanationRequestError,
  } = useMutation(updateExplanationRequest);
  // #endregion

  // #region Events
  useEffect(() => {
    if (explanationRequest) {
      resetFeedback({
        feedback: explanationRequest?.feedback ?? null,
        isOpened: explanationRequest?.isOpened,
        isEditAllowed: explanationRequest?.isEditAllowed,
      });
    }
  }, [explanationRequest]);

  const onFeedbackSubmit = (data) => {
    const formData = new FormData();

    formData.append('projectProposalId', projectProposalId);
    formData.append('id', explanationRequest?.id);

    if (data?.feedback) {
      formData.append('feedback', data.feedback);
    }
    formData.append('isOpened', data?.isOpened ? 'true' : 'false');
    formData.append('isEditAllowed', data?.isEditAllowed ? 'true' : 'false');

    // thêm cái này để không mất các commentFileUrls cũ
    // if (oldCommentFileUrls?.length > 0) {
    if (explanationRequest?.commentFileUrls?.length > 0) {
      formData.append('oldCommentFileUrls', JSON.stringify(explanationRequest?.commentFileUrls));
    }

    // if isSendMailAllowed = false => not send email request explanation
    // else: check some more conditions to send email request explanation
    formData.append('isSendMailAllowed', 'true');

    mutateUpdateExplanationRequest(formData, {
      onSuccess: () => {
        toast(
          TOAST.SUCCESS,
          data?.isOpened
            ? t('projectProposal.requestExplanation.requestUpdateSuccess')
            : t('projectProposal.requestExplanation.addFeedbackSuccess')
        );
        refetchExplanationRequests();
      },
      onError: () => {
        setIsVisibleDialogError(true);
      },
    });
  };

  const checkFeedbackValidation = () => {
    if (!isFeedbackValid) {
      toast(TOAST.ERROR, t('errorMessage.validationErrorMessage'));
    }
  };

  const handleDownloadFile = (path) => {
    mutateDownloadFile(
      { path, id: council?.id },
      {
        onSuccess: (res) => {
          if (res?.data) {
            const file = new Blob([res.data]);

            const link = document.createElement('a');
            link.href = URL.createObjectURL(file);
            link.download = formatFileName(path);

            link.click();
          }
        },
        onError: () => {},
      }
    );
  };

  const { data: allProjectProposalVersionsData } = useQuery(
    ['allProjectProposalVersions', projectProposalId],
    () => getAllVersions(projectProposalId)
  );

  const [financeVersion] = useMemo(() => {
    if (!allProjectProposalVersionsData?.data || !allProjectProposalVersionsData?.data?.length) {
      return [];
    }

    return [allProjectProposalVersionsData?.data?.find((item) => item?.version === 4) ?? null];
  }, [allProjectProposalVersionsData]);

  const {
    refetch: refetchCouncilMemberExcelFile,
    isLoading: isLoadingCouncilMemberExcelFile,
    isFetching: isFetchingCouncilMemberExcelFile,
  } = useQuery(
    ['downloadCouncilMemberExcelFile', council?.id, financeVersion?.id ?? projectProposalId],
    () => downloadCouncilMemberExcelFile(council?.id, financeVersion?.id ?? projectProposalId),
    {
      enabled: false,
      onSuccess: (res) => {
        autoDownloadFile(res);
      },
      onError: () => {
        toast(TOAST.ERROR, t('projectProposal.review.exportExcelErrorTitle'));
      },
    }
  );

  // #endregion

  const formatFeedbackStatus = (item) => {
    if (
      new Date(item?.createdAt)?.getTime() <
      new Date(explanationRequest?.explanationTime)?.getTime()
    ) {
      return <Tag value="Đã được giải trình" severity="success" rounded />;
    }
    return (
      <Tag
        value={explanationRequest?.isOpened ? 'Chưa được giải trình' : 'Không yêu cầu giải trình'}
        severity={explanationRequest?.isOpened ? 'warning' : 'success'}
        rounded
      />
    );
  };

  const [template] = useMemo(
    () => [generalInfoProject?.projectProposal?.template || null],
    [generalInfoProject]
  );
  if (isLoading) return <Loading />;

  return (
    <>
      {isErrorResDownloadFile && (
        <ErrorDialog
          title={t('council.edit.titleEditCouncilError')}
          isError={isErrorResDownloadFile}
          error={errorResDownloadFile}
          visible={isVisibleErrorDialog}
          setVisible={setIsVisibleDialogError}
        />
      )}
      <SendMailRemindAllMembersDialog
        visible={isVisibleConfirmSendRemind}
        setVisible={setIsVisibleConfirmSendRemind}
        refetch={refetchCouncil}
        projectProposalIdV1={projectProposalId}
        council={council}
      />

      {(isExplanationRequestsLoading || isUpdateExplanationRequestLoading) && <Loading />}

      <div className="bg-white p-5">
        {checkIsAllowedCRUDFromContract(
          generalInfoProject?.projectProposal?.projectType,
          roles
        ) && (
          <div className="flex justify-content-end align-items-center">
            <div className="flex gap-2">
              <Can do="read_member" on="council">
                <Button
                  icon="pi pi-download"
                  severity="success"
                  label={t('council.viewList.downloadCouncilMemberExcel')}
                  tooltipOptions={{ position: 'left' }}
                  onClick={() => {
                    refetchCouncilMemberExcelFile();
                  }}
                  disabled={
                    !council ||
                    council?.councilMembers?.length === 0 ||
                    isLoadingCouncilMemberExcelFile ||
                    isFetchingCouncilMemberExcelFile
                  }
                  loading={isLoadingCouncilMemberExcelFile || isFetchingCouncilMemberExcelFile}
                />
              </Can>

              <Can do="send_mail" on="council">
                <Button
                  icon="pi pi-envelope"
                  severity="success"
                  onClick={() => {
                    setIsVisibleConfirmSendRemind(true);
                  }}
                  tooltip={`${t('council.viewList.sendRemindMember')}
                                    ${t('council.fields.numberSentMail')}: ${
                    council?.projectProposalCouncils?.[0]?.numberSentMail ?? 0
                  }
                                    ${t('council.fields.lastSentMail')}: ${formatDatetime(
                    council?.projectProposalCouncils?.[0]?.lastSentMail,
                    'HH:mm:ss DD/MM/YYYY'
                  )}`}
                  tooltipOptions={{ position: 'left' }}
                  disabled={!council?.completedAt}
                />
              </Can>
              <Can do="create" on="council">
                {!council && !council?.deletedAt && (
                  <Link
                    to={`/projects-submit/${projectId}/${projectProposalId}/${summaryReportId}/council/create/${councilType?.id}`}
                  >
                    <Button
                      icon="pi pi-plus"
                      severity="primary"
                      tooltip={t('council.viewList.addCouncil')}
                      tooltipOptions={{ position: 'left' }}
                    />
                  </Link>
                )}
              </Can>
              <Can do="update" on="council">
                {council && !council?.deletedAt && (
                  <Link
                    to={`/projects-submit/${projectId}/${projectProposalId}/${summaryReportId}/council/edit/${council?.id}/${council?.councilType?.id}?key=editCouncil`}
                  >
                    <Button
                      icon="pi pi-pencil"
                      severity="warning"
                      tooltip={t('council.viewList.editCouncil')}
                      tooltipOptions={{ position: 'left' }}
                    />
                  </Link>
                )}
                <Button
                  icon="pi pi-check-square"
                  className="bg-teal-600 border-teal-600"
                  tooltip={t('council.viewList.editResult')}
                  tooltipOptions={{ position: 'left' }}
                  disabled={!council}
                  onClick={() => {
                    navigate(
                      `/projects-submit/${projectId}/${projectProposalId}/${summaryReportId}/council/edit/${council?.id}/${council?.councilType?.id}?key=editResult`
                    );
                  }}
                />
              </Can>
            </div>
          </div>
        )}

        <div className="m-0 bg-white p-5 p-fluid detail-info-council">
          <div className="flex flex-column mb-5">
            <h3
              className="line-height-3 text-4xl text-center font-semibold"
              style={{ marginBottom: '0px', paddingBottom: '1px' }}
            >
              {isLoading ? t('council.view.councilType.council') : 'HỘI ĐỒNG NGHIỆM THU'}
            </h3>

            {council && !council?.completedAt && (
              <h4 className="text-red-500 text-center">({t('council.view.notDoneAndSentMail')})</h4>
            )}
            {council &&
              council?.completedAt &&
              council?.councilMembers?.some((item) => !item?.isSentMail) && (
                <h4 className="text-blue-600 text-center">({t('council.view.onlyDone')})</h4>
              )}
          </div>

          <div className="ml-0 sm:ml-0 md:ml-4 lg:ml-4">
            <div className="pb-2">
              {/* Name and owner of project proposal */}
              <div className="text-lg mb-3">
                <h4 className="mb-3">{t('council.view.overviewLabel').toUpperCase()}</h4>
                <div className="ml-4 grid">
                  <div className="col-12">
                    <span className="font-semibold">
                      {t('projectProposal.general.projectNameAndType.vietnameseName')}:{' '}
                    </span>
                    <span>{generalInfoProject?.projectProposal?.vietnameseName}</span>
                  </div>
                  <div className="col-12">
                    <span className="font-semibold">
                      {t('projectProposal.general.projectNameAndType.englishName')}:{' '}
                    </span>
                    <span>{generalInfoProject?.projectProposal?.englishName}</span>
                  </div>
                  <div className="col-12">
                    <span className="font-semibold">
                      {t('projectProposal.general.ownerSection.labelCouncil')}:{' '}
                    </span>
                    <span>
                      {`${getAcadamicLevelShortHand(
                        owner?.academicRank,
                        owner?.academicDegree,
                        t,
                        true
                      )}${owner?.fullname ?? generalInfoProject?.owner?.fullname ?? ''}`}
                    </span>
                  </div>
                </div>
              </div>

              {/* Council info */}
              <div className="text-lg mb-3">
                <h4 className="mb-3">
                  {t('council.view.councilInfoLabel.acceptanceCouncil').toUpperCase()}
                </h4>
                <div className="ml-4 mb-2 grid">
                  <div className="col">
                    <span className="font-semibold">
                      {t('council.fields.name.acceptanceCouncil')}:{' '}
                    </span>
                    <span>{council?.name}</span>
                  </div>
                </div>
                <div className="ml-4 grid">
                  <div className="col-12 sm:col-6 md:col-6 lg:col-6">
                    <span className="font-semibold">{t('council.fields.establishDate')}: </span>
                    <span>
                      {council?.timeCreated &&
                        new Date(council?.timeCreated).toLocaleDateString('en-GB', {
                          timeZone: TIMEZONE_VN,
                        })}
                    </span>
                  </div>
                  <div className="col-12 sm:col-6 md:col-6 lg:col-6">
                    <span className="font-semibold">
                      {t('council.fields.decisionToEstablish')}:{' '}
                    </span>
                    {council?.projectProposalCouncils?.[0]?.establishmentFileUrl && (
                      <span
                        className="p-link text-primary underline"
                        onClick={() =>
                          handleDownloadFile(
                            council.projectProposalCouncils[0].establishmentFileUrl
                          )
                        }
                      >
                        {t('council.view.decision')}
                      </span>
                    )}
                  </div>
                  <div className="col-12 sm:col-6 md:col-6 lg:col-6">
                    <span className="font-semibold">{t('council.fields.reviewDeadline')}: </span>
                    {council?.projectProposalCouncils?.[0]?.reviewDeadline &&
                      new Date(
                        council?.projectProposalCouncils?.[0]?.reviewDeadline
                      ).toLocaleDateString('en-GB', { timeZone: TIMEZONE_VN })}
                  </div>
                  <div className="col-12 sm:col-6 md:col-6 lg:col-6">
                    <span className="font-semibold">{t('council.fields.meetingDate')}: </span>
                    <span>
                      {council?.projectProposalCouncils?.[0]?.timeMet &&
                        new Date(council?.projectProposalCouncils?.[0]?.timeMet).toLocaleDateString(
                          'en-GB',
                          { timeZone: TIMEZONE_VN }
                        )}
                    </span>
                  </div>
                  {!is1593(template) && (
                    <>
                      <div className="col-12 sm:col-6 md:col-6 lg:col-6">
                        <span className="font-semibold">{t('council.fields.totalScore')}: </span>
                        <span>{council?.projectProposalCouncils?.[0]?.scoreTotal}</span>
                      </div>
                      <div className="col-12 sm:col-6 md:col-6 lg:col-6">
                        <span className="font-semibold">{t('council.fields.averageScore')}: </span>
                        <span>
                          {roundNumber(council?.projectProposalCouncils?.[0]?.scoreAverage)}
                        </span>
                      </div>
                    </>
                  )}

                  <div className="col-12 sm:col-6 md:col-6 lg:col-6">
                    <span className="font-semibold">{t('council.fields.classification')}: </span>
                    <span>{`${t(
                      `council.viewList.${council?.projectProposalCouncils?.[0]?.classification}`
                    )}`}</span>
                  </div>
                  <div className="col-12 sm:col-6 md:col-6 lg:col-6">
                    <span className="font-semibold">{t('council.fields.result')}: </span>
                    <span>
                      {council?.projectProposalCouncils?.[0]?.councilStatus !== 'qualified' ? (
                        council?.projectProposalCouncils?.[0]?.councilStatus === 'notQualified' ? (
                          <span className="text-success">{t('council.viewList.notQualified')}</span>
                        ) : (
                          <span className="text-danger">{t('council.viewList.notReviewed')}</span>
                        )
                      ) : (
                        <span className="text-danger">{t('council.viewList.qualified')}</span>
                      )}
                    </span>
                  </div>
                </div>
                {/* Council members */}
                <div>
                  <h4 className="mb-3">{t('council.view.councilMemberLabel')}</h4>
                  <div className="ml-4">
                    {!isLoading && council?.councilMembers && (
                      <Members
                        refetch={refetchCouncil}
                        council={council}
                        members={council?.councilMembers}
                        councilType={council?.councilType}
                        organizations={organizations}
                        projectProposalId={projectProposalId}
                        councilId={council?.id}
                        reviewDeadline={council?.projectProposalCouncils?.[0]?.reviewDeadline}
                        isShowResult
                        isReadOnly={
                          !checkIsAllowedCRUDFromContract(
                            generalInfoProject?.projectProposal?.projectType,
                            roles
                          )
                        }
                        yearProposed={parseInt(
                          generalInfoProject?.projectProposal?.yearProposed,
                          10
                        )}
                        template={generalInfoProject?.projectProposal?.template}
                        hasProjects={false}
                      />
                    )}
                  </div>
                </div>
                {/* General conclusion */}
                <div>
                  <h4 className="mb-3">{t('council.view.conclusion.acceptanceCouncil')}</h4>
                  {council?.projectProposalCouncils?.[0]?.isApproved === false && (
                    <div className="ml-4">
                      <Message
                        severity="error"
                        text={t('council.view.refuse')}
                        className="w-max font-semibold"
                      />
                      <p className="mt-3">{t('council.view.reason')}</p>
                      <RichTextViewer
                        style={{ height: '200px', width: '100%' }}
                        value={council?.projectProposalCouncils?.[0]?.conclusion}
                        showHeader={false}
                        readOnly
                      />
                    </div>
                  )}
                  {council?.projectProposalCouncils?.[0]?.isApproved === true && (
                    <div className="ml-4">
                      <Message
                        severity="success"
                        text={t('council.view.approve')}
                        className="w-max font-semibold"
                      />
                      <p className="mt-3">{t('council.view.opinion')}:</p>
                      <p className="font-italic mt-4">{t('council.view.opinionDetail')}</p>
                      <RichTextViewer
                        style={{ height: '200px', width: '100%' }}
                        value={council?.projectProposalCouncils?.[0]?.conclusion}
                        showHeader={false}
                        readOnly
                      />
                    </div>
                  )}
                  {council?.projectProposalCouncils?.[0]?.isApproved === null &&
                    council?.projectProposalCouncils?.[0]?.conclusion && (
                      <div className="ml-4">
                        <RichTextViewer
                          style={{ height: '200px', width: '100%' }}
                          value={council?.projectProposalCouncils?.[0]?.conclusion}
                          showHeader={false}
                          readOnly
                        />
                      </div>
                    )}
                </div>
              </div>

              {/* Result minutes */}
              <div className="flex text-lg mt-2 flex-column">
                <h4 className="mb-3">
                  {t('council.view.appraisalCouncilMinutesLabel.acceptanceCouncil').toUpperCase()}
                </h4>
                <div className="flex ml-5 flex-column">
                  <div className="mb-2">
                    {council?.projectProposalCouncils?.[0]?.reportFileUrl ? (
                      <span
                        className="p-link text-primary underline"
                        onClick={() =>
                          handleDownloadFile(council?.projectProposalCouncils?.[0]?.reportFileUrl)
                        }
                      >
                        {t('council.view.appraisalCouncilMinutes')}
                      </span>
                    ) : (
                      <span className="font-italic">
                        {t('council.view.noAppraisalCouncilMinutes')}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Explanation */}
              {checkIsAllowedCRUDFromContract(
                generalInfoProject?.projectProposal?.projectType,
                roles
              ) &&
                explanationRequest && (
                  <div className="mt-2">
                    <div className="flex align-items-center justify-content-between mb-2">
                      <div className="flex align-items-center">
                        {(explanationRequest?.isOpened || explanationRequest?.ownerComment) && (
                          <h4>{t('projectProposal.requestExplanation.ownerExplanation')}</h4>
                        )}
                        {explanationRequest?.isOpened && !explanationRequest?.ownerComment && (
                          <Message
                            severity="error"
                            text={t('projectProposal.requestExplanation.ownerNotAddExplanationYet')}
                            className="w-max font-semibold ml-2 support-owner-explanation"
                          />
                        )}
                        {!explanationRequest?.isOpened && explanationRequest?.ownerComment && (
                          <Message
                            severity="success"
                            text={t('projectProposal.requestExplanation.updatedAfterFeedback')}
                            className="w-max font-semibold ml-2"
                          />
                        )}
                        {explanationRequest?.isOpened && explanationRequest?.ownerComment && (
                          <Message
                            severity="warn"
                            text={t('projectProposal.requestExplanation.notUpdatedAfterFeedback')}
                            className="w-max font-semibold ml-2"
                          />
                        )}
                      </div>
                      {explanationRequest?.explanationTime && (
                        <div className="w-auto flex justify-content-between text-lg">
                          <span className="mr-2 font-semibold">
                            {t('projectProposal.requestExplanation.updatedAt')}:
                          </span>
                          <span>
                            {new Date(explanationRequest?.explanationTime)?.toLocaleString(
                              'en-GB',
                              {
                                timeZone: TIMEZONE_VN,
                              }
                            )}
                          </span>
                        </div>
                      )}
                    </div>

                    {(explanationRequest?.isOpened || explanationRequest?.explanationTime) && (
                      <div className="ml-5 text-red-500 text-lg font-italic mb-2">
                        {t('projectProposal.requestExplanation.deadline')}:{' '}
                        {explanationRequest?.deadline
                          ? new Date(explanationRequest.deadline).toLocaleDateString('en-GB', {
                              timeZone: TIMEZONE_VN,
                            })
                          : t('projectProposal.requestExplanation.noDeadline')}
                      </div>
                    )}
                    <div className="ml-5 text-lg p-fluid">
                      {explanationRequest?.ownerComment && (
                        <>
                          {/* <Link to="compare-version?version=4">
                          <span className="underline">Xem so sánh với phiên bản trước</span>
                        </Link> */}
                          <div className="mb-2">
                            <span className="font-semibold">
                              {t('projectProposal.requestExplanation.comment')}:
                            </span>
                            <p
                              className="font-italic my-2 text-justify"
                              style={{ whiteSpace: 'pre-wrap' }}
                            >
                              {explanationRequest?.ownerComment}
                            </p>
                          </div>

                          <div className="mb-2">
                            <span className="mr-2 font-semibold">
                              {t('projectProposal.requestExplanation.commentFile')}:
                            </span>
                            {explanationRequest?.ownerCommentFileUrls?.length > 0 ? (
                              <ul className="list-disc text-lg">
                                {explanationRequest?.ownerCommentFileUrls?.map((fileUrl, index) => (
                                  <li className="mb-2" key={fileUrl?.id ?? index}>
                                    <span
                                      className="p-link text-primary underline"
                                      onClick={() => handleDownloadFile(fileUrl?.name)}
                                    >
                                      {formatFileName(fileUrl?.name)}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              t('projectProposal.requestExplanation.noCommentFile')
                            )}
                          </div>
                        </>
                      )}
                      {(explanationRequest?.isOpened || explanationRequest?.ownerComment) && (
                        <form onSubmit={handleSubmitFeedback(onFeedbackSubmit)}>
                          <div className="grid">
                            <label htmlFor="feedback" className="col-12 font-semibold">
                              {t('projectProposal.requestExplanation.specialistFeedback')}:
                            </label>
                            <div className="col-12">
                              <TextareaInput
                                name="feedback"
                                control={feedbackControl}
                                errors={feedbackErrors}
                                row={5}
                                disabled={
                                  council?.projectProposalCouncils?.[0]?.councilStatus !==
                                  'qualified'
                                }
                              />
                            </div>
                            {explanationRequest?.feedbacks?.length > 0 && (
                              <div className="form-table col-12">
                                <table style={{ borderCollapse: 'collapse' }}>
                                  <thead>
                                    <tr>
                                      <th style={{ textAlign: 'center' }}>
                                        {t('projectProposal.expenditure.expenditure.fields.order')}
                                      </th>
                                      <th
                                        style={{
                                          width: 'auto',
                                          minWidth: '24rem',
                                          textAlign: 'center',
                                        }}
                                      >
                                        {t('projectProposal.requestExplanation.feedback')}
                                      </th>
                                      <th style={{ minWidth: '6rem', textAlign: 'center' }}>
                                        {t('projectProposal.requestExplanation.time')}
                                      </th>
                                      <th style={{ minWidth: '6rem', textAlign: 'center' }}>
                                        {t('projectProposal.requestExplanation.status')}
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {explanationRequest?.feedbacks?.map((item, index) => (
                                      <tr key={item?.id ?? index}>
                                        <td className="text-center">{index + 1}</td>
                                        <td style={{ whiteSpace: 'pre-wrap' }}>{item?.content}</td>
                                        <td className="text-center">
                                          {/* eslint-disable indent */}
                                          {item?.createdAt
                                            ? new Date(item?.createdAt)?.toLocaleString('en-GB', {
                                                timeZone: TIMEZONE_VN,
                                              })
                                            : ''}
                                        </td>
                                        <td className="text-center">
                                          {formatFeedbackStatus(item)}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}

                            <label htmlFor="isOpened" className="col-12 font-semibold pb-0">
                              {t(
                                'projectProposal.requestExplanation.projectProposalRequireUpdateExplanation'
                              )}
                            </label>
                            <div className="col-12">
                              <RadioInput
                                name="isOpened"
                                control={feedbackControl}
                                errors={feedbackErrors}
                                options={[
                                  { value: true, name: t('formLayout.yes') },
                                  { value: false, name: t('formLayout.no') },
                                ]}
                                inputField="value"
                                disabled={
                                  council?.projectProposalCouncils?.[0]?.councilStatus !==
                                  'qualified'
                                }
                              />
                            </div>

                            {false && (
                              <>
                                <label
                                  htmlFor="isEditAllowed"
                                  className="col-12 font-semibold py-0"
                                >
                                  {t(
                                    'projectProposal.requestExplanation.allowOwnerEditProjectProposal'
                                  )}
                                </label>
                                <div className="col-12">
                                  <RadioInput
                                    name="isEditAllowed"
                                    control={feedbackControl}
                                    errors={feedbackErrors}
                                    options={[
                                      { value: true, name: t('formLayout.yes') },
                                      { value: false, name: t('formLayout.no') },
                                    ]}
                                    inputField="value"
                                    defaultValue={false}
                                    disabled={
                                      council?.projectProposalCouncils?.[0]?.councilStatus !==
                                      'qualified'
                                    }
                                  />
                                </div>
                              </>
                            )}
                          </div>
                          <div className="flex justify-content-end">
                            <Button
                              label={t('projectProposal.requestExplanation.feedback')}
                              severity="success"
                              type="submit"
                              // type="button"
                              onClick={checkFeedbackValidation}
                              disabled={
                                !(isFeedbackDirty && Object.keys(feedbackDirtyFields).length) ||
                                council?.projectProposalCouncils?.[0]?.councilStatus !== 'qualified'
                              }
                              className="w-8rem"
                            />
                          </div>
                        </form>
                      )}
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
